import moment from "moment-timezone";

import {
    HttpClient
} from "@angular/common/http";

import {
    inject,
    Injectable
} from "@angular/core";

import {
    TranslateService
} from "@ngx-translate/core";

import {
    firstValueFrom
} from "rxjs";

import {
    restManager
} from "app/ajs-upgraded-providers";

import {
    ConfigurationManagerService,
    EntityManagerService,
    UserActiveSessionManagerService
} from "src/app/core";

import {
    DevLogger
} from "src/app/shared/dev-logger";

import {
    groupBy,
    MathUtils,
    pickBy
} from "src/app/shared/utils";

import {
    DashboardData,
    DashboardDateRange,
    Payment,
    PrinterStatus,
    RawPrinter,
    ReducePrinter,
    StockRow,
    TodayDashboard,
    TodayPayments,
    VatGroup
} from "../models/dashboard.model";

const STOCK_FIELDS = new Set<string>(['combination_id', 'item_id', 'name', 'code', 'combination', 'options_values', 'barcode', 'unit', 'available', 'stock_quantity']);

/** Payments of this type are not sales payments: they're the "other changes" of the cash register. */
const OTHER_CHANGES_TYPE_ID = 99;

/** Fields that tell two payments apart: the payments with the same values for all of them are merged. */
const PAYMENT_IDENTITY = new Set<keyof Payment>(['name', 'unclaimed', 'payment_method_id', 'type_id', 'ticket_name']);

/** Printers that are always shown after the others. */
const OTHER_PRINTER_NAMES = ['Altro', 'other'];

/** Sum of the given fields of all the items (a missing item or value counts as 0). */
const sumFields = <T, K extends keyof T>(items: (T | undefined)[], fields: K[]) =>
    Object.fromEntries(fields.map(field => [
        field,
        MathUtils.round(items.reduce((sum, item) => sum + (Number(item?.[field]) || 0), 0))
    ])) as Record<K, number>;

/** Merges the rows with the same key in one: the `identity` fields are taken from the first one, the `summed` ones are added up. */
const mergeRows = <T extends object, I extends keyof T, S extends keyof T>(rows: T[], keyOf: (row: T) => unknown, identity: Iterable<I>, summed: S[]) => {
    const identityFields = new Set<keyof T>(identity);

    return Object.values(groupBy(rows, keyOf)).map(group =>
        ({ ...pickBy(group[0], (_, field) => identityFields.has(field)), ...sumFields(group, summed) }) as Pick<T, I | S>);
};

/** The "total" printer: the sum of all the others. */
const mergePrinters = (printers: RawPrinter[]): RawPrinter => {
    const all = <T>(pick: (printer: RawPrinter) => T[] | undefined) => printers.flatMap(printer => pick(printer) ?? []);
    const vatGroup = (group: 'fiscal_receipts' | 'invoices' | 'summary_rc_invoices' | 'summary_nrc_invoices', withUnclaimed: boolean): VatGroup => ({
        ...sumFields(printers.map(printer => printer[group]), withUnclaimed ? ['count', 'unclaimed'] : ['count']),
        vats: mergeRows(all(printer => printer[group]?.vats), vat => vat.name, ['name'], ['amount', 'net_amount', 'vat_amount'])
    });

    return {
        fiscal_receipts: vatGroup('fiscal_receipts', true),
        invoices: vatGroup('invoices', true),
        summary_rc_invoices: vatGroup('summary_rc_invoices', false),
        summary_nrc_invoices: vatGroup('summary_nrc_invoices', false),
        refunds: sumFields(printers.map(printer => printer.refunds), ['amount', 'count']),
        voids: sumFields(printers.map(printer => printer.voids), ['amount', 'count']),
        food_cost: sumFields(printers, ['food_cost']).food_cost,
        cash_movements: {
            ...sumFields(printers.map(printer => printer.cash_movements), ['sales']),
            tickets: mergeRows(all(printer => printer.cash_movements?.tickets), ticket => ticket.name, ['name'], ['count', 'amount'])
        },
        departments: mergeRows(all(printer => printer.departments), department => department.name, ['name'], ['amount']),
        channels: Object.fromEntries(
            mergeRows(all(printer => Object.values(printer.channels ?? {})), channel => channel.channel, ['channel'], ['count', 'amount'])
                .map(channel => [channel.channel, channel])
        ),
        payments: mergeRows(all(printer => printer.payments), payment => JSON.stringify(pickBy(payment, (_, field) => PAYMENT_IDENTITY.has(field))), PAYMENT_IDENTITY, ['amount']),
        price_changes: mergeRows(all(printer => printer.price_changes), change => change.type, ['type'], ['amount'])
    };
};

interface WeatherResponse {
    error: string;
    response: {
        status_code: number | string;
        t_min: number;
        t_max: number;
        status_description: string;
    };
}

/**
 * Loads (and caches) the data shown in the dashboard.
 */
@Injectable({
    providedIn: 'root'
})
export class DashboardDataManagerService {
    private readonly configurationManagerService = inject(ConfigurationManagerService);
    private readonly entityManagerService = inject(EntityManagerService);
    private readonly http = inject(HttpClient);
    private readonly restManagerService = inject(restManager);
    private readonly translateService = inject(TranslateService);
    private readonly userActiveSessionManagerService = inject(UserActiveSessionManagerService);

    private dataCache?: DashboardData;

    /**
     * Gets a copy of the data of the current day loaded by the last successful `fetchData`
     * @returns The data, or undefined if it was never loaded
     */
    public getDataCache() {
        return this.dataCache && structuredClone(this.dataCache);
    }

    /**
     * Gets the list of printers to show: the "total" one first (it contains everything the other printers have),
     * then all the others sorted by name (the "other" ones at the end).
     * @param printersByName The printers returned by `analytics/printers`
     */
    public getMergedResources(printersByName: Record<string, RawPrinter> | undefined): ReducePrinter[] {
        const printers = Object.entries(printersByName ?? {})
            .map(([name, printer]) => ({ ...printer, name }))
            .sort((a, b) => a.name < b.name ? -1 : a.name > b.name ? 1 : 0);
        const isOther = (printer: { name: string }) => OTHER_PRINTER_NAMES.includes(printer.name);

        return [
            { ...mergePrinters(printers), name: this.translateService.instant('DASHBOARD.TOTAL') },
            ...printers.filter(printer => !isOther(printer)),
            ...printers.filter(isOther)
        ].map(printer => ({
            ...printer,
            other_changes_amount: (printer.payments ?? []).filter(payment => payment.type_id === OTHER_CHANGES_TYPE_ID).reduce((sum, payment) => sum + (payment.amount || 0), 0)
        }));
    }

    /**
     * Loads the data of a day (the current one if no range is given).
     * A failure of a single request is only logged: the data it would have provided will be missing.
     * @param customDateRange The interval to load
     */
    public async fetchData(customDateRange?: DashboardDateRange): Promise<DashboardData> {
        const dateRange = customDateRange ?? this.getDateRange();
        const query = { date_since: dateRange.dayStart, date_max: dateRange.dayEnd };

        // The requests are independent. Only the stocks can make this method fail (as it always did):
        // all the other ones handle their own errors, so a failure there just leaves the data out.
        const [stocks, printersStatus, weather, dashboard, payments, printers] = await Promise.all([
            this.getStocks(),
            this.getPrintersStatus(),
            this.getWeather(),
            this.getAnalytics<TodayDashboard>('analytics/dashboard', query),
            this.getAnalytics<TodayPayments>('analytics/payments', query),
            this.getAnalytics<{ printers: Record<string, RawPrinter> }>('analytics/printers', query)
        ]);

        const data = {
            stocks,
            printers_status: printersStatus,
            today_weather: weather,
            today_dashboard: dashboard,
            today_payments: payments,
            today_printers: printers?.printers
        } as DashboardData;

        // The cache is only meant to show the data of the current day right away when the dashboard is opened:
        // other days are not cached, and neither is a result without the main data (it would replace a usable one).
        // The cache holds a copy so the caller can't alter it through the returned object.
        if (!customDateRange && data.today_dashboard?.cash_movements?.total) {
            this.dataCache = structuredClone(data);
        }

        return data;
    }

    private getDateRange(): DashboardDateRange {
        const dayStartTime = this.configurationManagerService.getDayStartTime();

        return {
            dayStart: moment(dayStartTime).toISOString(),
            dayEnd: moment(dayStartTime).add(1, 'day').toISOString()
        };
    }

    private async getStocks() {
        const stocks = new Map<string, StockRow>();

        for (const stock of await this.entityManagerService.stock.fetchCollectionOffline()) {
            if (stock.combination_id) {
                stocks.set(`combination_id_${stock.combination_id}`, pickBy(stock, (_, field) => STOCK_FIELDS.has(field)));
            } else if (stock.item_id) {
                stocks.set(`item_id_${stock.item_id}`, pickBy(stock, (_, field) => STOCK_FIELDS.has(field)));
            }
        }

        return [...stocks.values()];
    }

    private async getPrintersStatus() {
        //TODO: this endpoint is broken, find a replacement or drop it
        try {
            const shopName = this.userActiveSessionManagerService.getSession()?.shop?.name;
            const response = await firstValueFrom(this.http.get<{ results?: PrinterStatus[] }>(`https://printerremotemanager.scloby.com/ps/query/?shop=${shopName}`));

            return response?.results;
        } catch {
            return undefined;
        }
    }

    private async getWeather() {
        try {
            const city = this.configurationManagerService.getPreference('general.address_city');

            if (city == null) {
                return undefined;
            }

            const { error, response } = await firstValueFrom(this.http.get<WeatherResponse>(`https://weatherapi.scloby.com/meteo/?city=${city}`));

            if (error !== "false") {
                return undefined;
            }

            return {
                status_code: response.status_code,
                t_min: response.t_min,
                t_max: response.t_max,
                text: response.status_description
            };
        } catch {
            return undefined;
        }
    }

    private async getAnalytics<T>(endpoint: string, query: { date_since: string; date_max: string }): Promise<T | undefined> {
        try {
            return await this.restManagerService.getList(endpoint, query);
        } catch (error) {
            DevLogger.log("errors from dashboard", error);

            return undefined;
        }
    }
}
